import { SearchResultSection, EnlargedSearchResultSection } from '@atlassian/search-dialog';
import React from 'react';
import { injectIntl } from 'react-intl-next';
import { SearchDialogAnalyticsContext } from '../../common/analytics';
import { hasSearchResults } from '../product-router/product/utils';
import { SearchResultItem } from './search-result-item';
import { ViewAllLink } from './view-all-link';
import { PersistRecentSearch } from '../recent-searches/persist-recent-search';
import { useFeaturesContext } from '../features-context/features-context';
import { SearchResultWrapper } from './search-result-wrapper';
import { useProductContext } from '../product-router';
export var INITIAL_ITEM_LIMIT = 10;
export var getTotalNumberOfItemsInPreviousSections = function getTotalNumberOfItemsInPreviousSections(searchResultSections, sectionIndex) {
  return searchResultSections.slice(0, sectionIndex).reduce(function (accumulator, currentElem) {
    return accumulator + Math.min(currentElem.searchResults.length, currentElem.resultLimit || INITIAL_ITEM_LIMIT);
  }, 0);
};
var getTotalResultCount = function getTotalResultCount(forceHideResultCount, section) {
  // resultCount is only shown when a size is specified. We changed this
  // from using searchResults.length as we want to indicate the number of
  // possible search results rather than just the ones shown.
  var resultCount = !forceHideResultCount && section.showResultCount && section.size && section.searchResults.length ? Math.max(section.size, section.searchResults.length) : undefined;

  // totalResults considers the count returned from a section's resultMapper
  // to ensure rendered results align with the displayed count, in case results shown
  // are limited by the resultMapper
  var totalResults = Boolean(resultCount && section.searchResults.length < (section.resultLimit || INITIAL_ITEM_LIMIT) && resultCount >= section.searchResults.length) ? section.searchResults.length : resultCount;
  return totalResults;
};
var GenericSearchResultWithIntl = function GenericSearchResultWithIntl(_ref) {
  var children = _ref.children,
    _ref$forceHideResultC = _ref.forceHideResultCount,
    forceHideResultCount = _ref$forceHideResultC === void 0 ? false : _ref$forceHideResultC,
    _ref$generateSectionT = _ref.generateSectionTitle,
    generateSectionTitle = _ref$generateSectionT === void 0 ? function (_ref2) {
      var title = _ref2.title;
      return title;
    } : _ref$generateSectionT,
    intl = _ref.intl,
    isCollapsed = _ref.isCollapsed,
    linkComponent = _ref.linkComponent,
    productState = _ref.productState,
    renderSectionViewAll = _ref.renderSectionViewAll,
    searchItems = _ref.searchItems,
    screen = _ref.screen,
    nonPrivacySafeAnalyticContext = _ref.nonPrivacySafeAnalyticContext,
    appendQueryParams = _ref.appendQueryParams;
  var _useFeaturesContext = useFeaturesContext(),
    quickFind = _useFeaturesContext.quickFind;
  var _useProductContext = useProductContext(),
    getActiveProduct = _useProductContext.getActiveProduct;
  var activeProduct = getActiveProduct();
  if (!searchItems || !hasSearchResults(searchItems)) {
    // This is mainly used to handle the loading state (e.g. a spinner or a skeleton)
    if (children) {
      return /*#__PURE__*/React.createElement(SearchResultWrapper, null, children);
    }
    return null;
  }
  var ResultSection = quickFind !== null && quickFind !== void 0 && quickFind.enabled ? EnlargedSearchResultSection : SearchResultSection;
  var QuickFindPersistRecentSearch = quickFind !== null && quickFind !== void 0 && quickFind.enabled ? React.Fragment : PersistRecentSearch;
  var ItemSection = function ItemSection(props) {
    var _section$searchResult;
    var section = props.section,
      sectionIndex = props.sectionIndex;
    var itemLimit = typeof section.resultLimit === 'number' ? section.resultLimit : INITIAL_ITEM_LIMIT;
    var searchResults = ((_section$searchResult = section.searchResults) === null || _section$searchResult === void 0 ? void 0 : _section$searchResult.slice(0, itemLimit)) || [];
    var totalResults = getTotalResultCount(forceHideResultCount, section);
    var totalNumberOfItemsInPreviousSections = getTotalNumberOfItemsInPreviousSections(searchItems.sections, sectionIndex);
    if (searchResults.length === 0) {
      return null;
    }
    var recentSearchResultsLength = searchResults.filter(function (result) {
      return result.isRecentResult;
    }).length;
    return /*#__PURE__*/React.createElement(ResultSection, {
      totalResults: totalResults,
      title: section.titleRenderer ? section.titleRenderer({
        isCollapsed: isCollapsed,
        productState: productState
      }) : generateSectionTitle(section, intl)
    }, searchResults.map(function (result, index) {
      var _activeProduct$id;
      var analyticContext = {
        sectionIndex: sectionIndex,
        sectionId: section.id,
        containerId: result.containerId || null,
        indexWithinSection: index,
        searchResultRelativeIndex: index - recentSearchResultsLength >= 0 ? index - recentSearchResultsLength : null,
        globalIndex: totalNumberOfItemsInPreviousSections + index,
        isCached: result.isRecentResult || false,
        activeProduct: (_activeProduct$id = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.id) !== null && _activeProduct$id !== void 0 ? _activeProduct$id : '',
        metadata: {
          contentId: result.id,
          containerId: result.containerId || null,
          contentType: result.type || null
        }
      };
      return /*#__PURE__*/React.createElement(SearchDialogAnalyticsContext, {
        key: result.id,
        analyticContext: analyticContext,
        nonPrivacySafeAnalyticContext: nonPrivacySafeAnalyticContext || {}
      }, /*#__PURE__*/React.createElement(QuickFindPersistRecentSearch, null, /*#__PURE__*/React.createElement(SearchResultItem, {
        isCollapsed: isCollapsed,
        linkComponent: linkComponent,
        productState: productState,
        resultRenderer: section.resultRenderer,
        resultWrapper: section.resultWrapper,
        screen: screen,
        searchResult: result,
        appendQueryParams: appendQueryParams,
        useAnalyticsOverride: section.useAnalyticsOverride
      })));
    }), renderSectionViewAll && section.viewAllLinkGenerator && /*#__PURE__*/React.createElement(ViewAllLink, {
      linkComponent: linkComponent,
      section: section,
      totalResults: totalResults
    }));
  };
  return /*#__PURE__*/React.createElement(SearchResultWrapper, null, searchItems.sections.map(function (section, index) {
    return /*#__PURE__*/React.createElement(ItemSection, {
      section: section,
      sectionIndex: index,
      key: section.id
    });
  }), children);
};
export var GenericSearchResult = injectIntl(GenericSearchResultWithIntl);